// v2.19.29b — Onglet Paramètres dans /mon-compte.
// Centralise les préférences UI persistées en localStorage (déplacées depuis le drawer).
import { useEffect, useState } from "react";

const LS_KEYS = {
  themeMode: "civ_ai_theme_mode",
  llmProfile: "civ_ai_llm_profile",
  voiceOutput: "civ_voice_output_enabled",
  theme: "civ_theme", // dark / light
};

const LLM_PROFILES = [
  { k: "casual", l: "🟢 Casual" },
  { k: "street", l: "🔴 Nouchi (street)" },
  { k: "equilibre", l: "🔵 Équilibré" },
  { k: "precis", l: "🎯 Précis" },
  { k: "creatif", l: "🟣 Créatif" },
];

function _readLs(key, fallback) {
  try {
    const v = localStorage.getItem(key);
    if (v === null || v === undefined) return fallback;
    if (v === "true") return true;
    if (v === "false") return false;
    return v;
  } catch (_) { return fallback; }
}

function _writeLs(key, value) {
  try { localStorage.setItem(key, String(value)); } catch (_) { /* ignore */ }
  // Dispatch un event que les autres composants peuvent capturer si besoin.
  try { window.dispatchEvent(new StorageEvent("storage", { key, newValue: String(value) })); } catch (_) {}
}

export default function SettingsTab() {
  const [theme, setTheme] = useState(() => _readLs(LS_KEYS.theme, "dark"));
  const [llmProfile, setLlmProfile] = useState(() => _readLs(LS_KEYS.llmProfile, "casual"));
  const [voiceOutput, setVoiceOutput] = useState(() => _readLs(LS_KEYS.voiceOutput, false));

  useEffect(() => { _writeLs(LS_KEYS.theme, theme); document.documentElement.setAttribute("data-theme", theme); }, [theme]);
  useEffect(() => { _writeLs(LS_KEYS.llmProfile, llmProfile); }, [llmProfile]);
  useEffect(() => { _writeLs(LS_KEYS.voiceOutput, voiceOutput); }, [voiceOutput]);

  return (
    <div data-testid="settings-tab" style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      {/* THÈME */}
      <section style={sectionStyle}>
        <h3 style={h3Style}>🌗 Apparence</h3>
        <div style={rowStyle}>
          <span style={labelStyle}>Thème de l’app</span>
          <div style={{ display: "flex", gap: 6 }}>
            <button
              type="button"
              data-testid="settings-theme-dark"
              onClick={() => setTheme("dark")}
              style={pillStyle(theme === "dark")}
            >🌙 Sombre</button>
            <button
              type="button"
              data-testid="settings-theme-light"
              onClick={() => setTheme("light")}
              style={pillStyle(theme === "light")}
            >☀️ Clair</button>
          </div>
        </div>
      </section>

      {/* PROFIL LLM */}
      <section style={sectionStyle}>
        <h3 style={h3Style}>🎛 Profil de réponse</h3>
        <p style={subStyle}>Influence le style des réponses d’AYA (ton, formalité, créativité).</p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 6 }}>
          {LLM_PROFILES.map((p) => (
            <button
              key={p.k}
              type="button"
              data-testid={`settings-llm-${p.k}`}
              onClick={() => setLlmProfile(p.k)}
              style={pillStyle(llmProfile === p.k)}
            >{p.l}</button>
          ))}
        </div>
      </section>

      {/* VOIX */}
      <section style={sectionStyle}>
        <h3 style={h3Style}>🔊 Voix</h3>
        <div style={rowStyle}>
          <span style={labelStyle}>Lecture vocale automatique</span>
          <button
            type="button"
            data-testid="settings-voice-toggle"
            onClick={() => setVoiceOutput((v) => !v)}
            style={{
              ...pillStyle(voiceOutput),
              minWidth: 90,
            }}
          >{voiceOutput ? "✅ Activée" : "🔇 Désactivée"}</button>
        </div>
      </section>

      <p style={{ fontSize: 11, color: "#94A3B8", margin: "8px 0 0", fontStyle: "italic" }}>
        Les modifications sont sauvegardées automatiquement et appliquées au prochain chargement de l’app.
      </p>
    </div>
  );
}

const sectionStyle = {
  padding: "14px 16px",
  background: "rgba(255,255,255,0.04)",
  border: "1px solid rgba(255,255,255,0.10)",
  borderRadius: 12,
};

const h3Style = { margin: "0 0 6px", fontSize: 14, fontWeight: 700, color: "#F1F5F9" };
const subStyle = { margin: "0 0 10px", fontSize: 12, color: "rgba(255,255,255,0.65)" };
const rowStyle = { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" };
const labelStyle = { fontSize: 13, color: "rgba(255,255,255,0.85)" };

const pillStyle = (active) => ({
  padding: "8px 14px",
  borderRadius: 999,
  border: `1.5px solid ${active ? "#FBBF24" : "rgba(255,255,255,0.15)"}`,
  background: active ? "rgba(251,191,36,0.18)" : "rgba(255,255,255,0.04)",
  color: active ? "#FBBF24" : "#E8ECF7",
  fontSize: 12.5,
  fontWeight: 700,
  cursor: "pointer",
  fontFamily: "inherit",
});
